import React from 'react';
import { useSelectedElement } from '../hooks/useTemplateStore';
import { Settings, X, Shield } from 'lucide-react';
import { Button } from '@/ui-components/ui/button';
import { CircleProperties } from './properties/circle';
import { RectProperties } from './properties/rect';
import { TextProperties } from './properties/text';
import { ImageProperties } from './properties/image';
import { SvgProperties } from './properties/svg';
import { cn } from '@/lib/utils/cn';
import { usePermissions } from '@/lib/hooks/use-permissions';
import { Permission } from '@/lib/api/interfaces/permissions.interface';

interface PropertiesPanelProps {
  className?: string;
  onClose?: () => void;
  showCloseButton?: boolean;
  onOpenSlotPanel?: () => void;
}

/**
 * Properties Panel Component
 * Displays and allows editing of element properties based on element type
 */
export const PropertiesPanel: React.FC<PropertiesPanelProps> = ({
  className,
  onClose,
  showCloseButton = false,
  onOpenSlotPanel
}) => {
  const selectedElement = useSelectedElement();
  const { can } = usePermissions();

  if (!selectedElement) {
    return (
      <div className={cn("w-full bg-white h-full flex flex-col", className)}>
        <div className="px-3 py-3 flex-shrink-0 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Settings className="w-4 h-4 text-gray-500" />
              <h2 className="text-sm font-medium text-gray-700">Eigenschaften</h2>
            </div>
            {showCloseButton && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="h-6 w-6 p-0 text-gray-400 hover:text-gray-600 hover:bg-gray-100"
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <p className="text-xs text-gray-400">Wählen Sie ein Element aus</p>
        </div>
      </div>
    );
  }

  // Check for watermark protection
  if (selectedElement.isWatermark && !can(Permission.EDIT_WATERMARKS)) {
    return (
      <div className={cn("w-full bg-white h-full flex flex-col", className)}>
        {/* Header */}
        <div className="px-3 py-3 flex-shrink-0 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-blue-500" />
              <h2 className="text-sm font-medium text-gray-700">Geschützt</h2>
            </div>
            {showCloseButton && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="h-6 w-6 p-0 text-gray-400 hover:text-gray-600 hover:bg-gray-100"
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>

        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <Shield className="w-12 h-12 text-gray-200 mb-3" />
          <h3 className="text-sm font-medium text-gray-900">Wasserzeichen</h3>
          <p className="text-xs text-gray-500 mt-1">
            Dieses Element ist Teil des Designs und kann nicht bearbeitet werden.
          </p>
        </div>
      </div>
    );
  }

  // Pass onOpenSlotPanel to property components that need it
  const renderElementProperties = () => {
    switch (selectedElement.type) {
      case 'text':
        return <TextProperties element={selectedElement} onOpenSlotPanel={onOpenSlotPanel} />;
      case 'rect':
        return <RectProperties element={selectedElement} onOpenSlotPanel={onOpenSlotPanel} />;
      case 'circle':
        return <CircleProperties element={selectedElement} onOpenSlotPanel={onOpenSlotPanel} />;
      case 'image':
        return <ImageProperties element={selectedElement} onOpenSlotPanel={onOpenSlotPanel} />;
      case 'svg':
        return <SvgProperties element={selectedElement} onOpenSlotPanel={onOpenSlotPanel} />;
      default:
        return null;
    }
  };

  return (
    <div className={cn("w-full bg-white h-full flex flex-col", className)}>
      {/* Header */}
      <div className="px-3 py-3 flex-shrink-0 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Settings className="w-4 h-4 text-gray-500" />
            <h2 className="text-sm font-medium text-gray-700">Eigenschaften</h2>
            
            <div className="inline-flex items-center gap-1.5 px-2 py-1 bg-blue-50 rounded text-xs font-medium text-blue-700">
              {selectedElement.type.charAt(0).toUpperCase() + selectedElement.type.slice(1)}
            </div>
          </div>
          {showCloseButton && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-6 w-6 p-0 text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-3 pt-3 pb-3 space-y-4">
        {renderElementProperties()}
      </div>
    </div>
  );
};